// ✅ src/pages/VehicleV2/BillAddAdvance.jsx  (UPDATED FULL FILE)
// Supports:
// - ASPX-style querystring: ?VehID=2142&Type=1|2
// - Type 1 = Advance, Type 2 = Shifting
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + gridLoading + billingLoading + savingLoading + deletingLoading states
// - cleaner init / list / save / delete / bill flow
// - consistent card / section / button style
//
// Backend expected:
//   GET  /api/billAddAdvance/init?vehicleId=&type=&branchId=&loginUserId=&billNo=&page=&pageSize=
//   GET  /api/billAddAdvance/list?vehicleId=&type=&branchId=&loginUserId=&billNo=&page=&pageSize=
//   POST /api/billAddAdvance/save
//   POST /api/billAddAdvance/deleteSelected
//   POST /api/billAddAdvance/addSelectedToBill

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaTrash,
  FaFileInvoiceDollar,
  FaSyncAlt,
  FaEdit,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/billAddAdvance";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const isValidDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);

const pad2 = (n) => String(n).padStart(2, "0");

const isoToDDMMYYYY = (isoOrDateStr) => {
  if (!isoOrDateStr) return "";
  if (isValidDDMMYYYY(isoOrDateStr)) return isoOrDateStr;

  const d = new Date(isoOrDateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const ddmmyyyyToInputDate = (ddmmyyyy) => {
  if (!isValidDDMMYYYY(ddmmyyyy)) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const inputDateToDDMMYYYY = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [yyyy, mm, dd] = String(yyyyMmDd).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const defaultForm = {
  id: 0,
  dateInput: "",
  amount: "",
};

const mapRow = (r) => ({
  id: toInt(r?.fld_id ?? r?.id ?? 0, 0),
  date: toStr(r?.fld_date ?? r?.date),
  amount: toStr(r?.fld_amount ?? r?.amount),
});

const BillAddAdvance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehicleId =
    toInt(params.get("VehID")) ||
    toInt(params.get("VehId")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;

  // 1 = Advance, 2 = Shifting
  const type =
    toInt(params.get("Type")) ||
    toInt(params.get("type")) ||
    0;

  const billNo =
    toStr(params.get("billNo")) ||
    toStr(params.get("BillNo")) ||
    toStr(location.state?.billNo) ||
    "";

  const STORAGE_KEY = useMemo(
    () => `billGen_advshifting_${vehicleId}_${type}`,
    [vehicleId, type]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);

  const [pageTitle, setPageTitle] = useState("Bill Add Advance / Shifting");
  const [form, setForm] = useState(defaultForm);

  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const isBusy =
    pageLoading ||
    gridLoading ||
    billingLoading ||
    savingLoading ||
    deletingLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const valueBoxClass = useMemo(
    () =>
      `px-3 py-2 rounded-lg border ${
        isDark
          ? "bg-gray-700/20 border-gray-600 text-gray-200"
          : "bg-gray-50 border-gray-200 text-gray-800"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- derived ---------------- */
  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);
  const totalPages = useMemo(() => {
    const t = toInt(total, 0);
    return Math.max(1, Math.ceil(t / pageSize));
  }, [total, pageSize]);

  const entryTypeLabel = useMemo(() => {
    if (type === 1) return "Advance";
    if (type === 2) return "Shifting";
    return "-";
  }, [type]);

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const clearForm = useCallback(() => {
    setForm(defaultForm);
  }, []);

  const getRowId = useCallback((row, idx) => {
    return toInt(row?.id ?? row?.fld_id ?? idx + 1, 0);
  }, []);

  const saveSelectionToLocalStorage = useCallback(
    (ids, totalAmount) => {
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...existing,
            ids: Array.isArray(ids) ? ids.map((x) => Number(x) || 0) : [],
            totalAmount: String(totalAmount ?? 0),
            type,
            vehicleId,
            billNo,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }
    },
    [STORAGE_KEY, type, vehicleId, billNo]
  );

  const goToBillGenerationWithData = useCallback(
    (handoff, ids, totalAmount) => {
      const payload = {
        vehicleId,
        type,
        billNo,
        ...handoff,
      };

      sessionStorage.setItem(
        "BillGen_AdvanceShiftingHandoff",
        JSON.stringify(payload)
      );

      saveSelectionToLocalStorage(ids, totalAmount);

      navigate(
        `/vehicle/BillGeneration?VehID=${encodeURIComponent(
          vehicleId
        )}&Type=${encodeURIComponent(type)}`,
        {
          state: payload,
        }
      );
    },
    [vehicleId, type, billNo, navigate, saveSelectionToLocalStorage]
  );

  /* ---------------- validators ---------------- */
  const validateForm = useCallback(() => {
    const ddmmyyyy = inputDateToDDMMYYYY(form.dateInput);

    if (!ddmmyyyy) {
      return canUse(false, "Please enter Date");
    }

    const amt = Number(form.amount);
    if (!form.amount || !Number.isFinite(amt) || amt <= 0) {
      return canUse(false, "Please enter valid Amount");
    }

    return true;
  }, [form, canUse]);

  /* ---------------- API loaders ---------------- */
  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehicleId, "VehID missing in URL. Use ?VehID=2142&Type=1")) return;
    if (!canUse(type === 1 || type === 2, "Type must be 1 or 2 in URL")) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          type,
          branchId,
          loginUserId,
          billNo,
          page: 1,
          pageSize,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      if (toStr(data?.title)) {
        setPageTitle(toStr(data.title));
      } else {
        setPageTitle(type === 1 ? "ADD ADVANCE" : "ADD SHIFTING");
      }

      const rows = (data?.list || []).map(mapRow);
      setList(rows);
      setTotal(toInt(data?.total, rows.length));
      setPage(toInt(data?.page, 1) || 1);

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          const ids = Array.isArray(saved?.ids) ? saved.ids : [];
          const map = {};
          ids.forEach((id) => {
            const n = toInt(id, 0);
            if (n > 0) map[n] = { id: n };
          });
          setSelected(map);
        } else {
          setSelected({});
        }
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleId,
    type,
    billNo,
    pageSize,
    commonHeaders,
    canUse,
    STORAGE_KEY,
  ]);

  const loadList = useCallback(
    async (nextPage = 1) => {
      if (!canUse(vehicleId, "VehID missing in URL")) return;
      if (!canUse(type === 1 || type === 2, "Type must be 1 or 2")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            vehicleId,
            type,
            branchId,
            loginUserId,
            billNo,
            page: nextPage,
            pageSize,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          setList([]);
          setTotal(0);
          setPage(nextPage);
          toast.warn(data?.message || "No data found");
          return;
        }

        const rows = (data?.list || []).map(mapRow);
        setList(rows);
        setTotal(toInt(data?.total, rows.length));
        setPage(toInt(data?.page, nextPage) || nextPage);
        setSelected({});
      } catch (err) {
        console.error(err);
        setList([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [
      vehicleId,
      type,
      branchId,
      loginUserId,
      billNo,
      pageSize,
      commonHeaders,
      canUse,
    ]
  );

  /* ---------------- handlers ---------------- */
  const handleRefresh = useCallback(async () => {
    clearForm();
    setSelected({});
    await loadInit();
  }, [clearForm, loadInit]);

  const handleBack = useCallback(() => {
    navigate(`/vehicle/BillGeneration?VehID=${vehicleId}&Type=${type}`);
  }, [navigate, vehicleId, type]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    if (!canUse(vehicleId, "VehID missing in URL")) return;
    if (!canUse(type === 1 || type === 2, "Type must be 1 or 2")) return;

    const payload = {
      branchId,
      loginUserId,
      vehicleId,
      type,
      billNo,
      id: form.id ? toInt(form.id) : 0,
      date: inputDateToDDMMYYYY(form.dateInput),
      amount: Number(form.amount),
    };

    try {
      setSavingLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(
        data?.message ||
          (payload.id ? "Updated successfully" : "Inserted successfully")
      );

      clearForm();
      await loadList(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSavingLoading(false);
    }
  }, [
    validateForm,
    canUse,
    vehicleId,
    type,
    branchId,
    loginUserId,
    billNo,
    form,
    commonHeaders,
    clearForm,
    loadList,
  ]);

  const handleEditRow = useCallback((row) => {
    const id = toInt(row?.id ?? row?.fld_id, 0);
    const ddmmyyyy = isoToDDMMYYYY(row?.date ?? row?.fld_date);

    setForm({
      id,
      dateInput: ddmmyyyyToInputDate(ddmmyyyy),
      amount: toStr(row?.amount ?? row?.fld_amount ?? ""),
    });

    toast.info(`Editing ID: ${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleRow = useCallback(
    (row, idx) => {
      const id = getRowId(row, idx);
      setSelected((p) => {
        const next = { ...p };
        if (next[id] !== undefined) delete next[id];
        else next[id] = { id };
        return next;
      });
    },
    [getRowId]
  );

  const handleDeleteSelected = useCallback(async () => {
    const ids = Object.values(selected)
      .map((x) => toInt(x?.id, 0))
      .filter((x) => x > 0);

    if (!canUse(ids.length > 0, "Please select any one")) return;
    if (!window.confirm("Are you sure?")) return;

    try {
      setDeletingLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/deleteSelected`,
        {
          branchId,
          loginUserId,
          vehicleId,
          type,
          billNo,
          ids,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data?.message || "Deleted Successfully");
      await loadList(page);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Delete failed"
      );
    } finally {
      setDeletingLoading(false);
    }
  }, [
    selected,
    canUse,
    branchId,
    loginUserId,
    vehicleId,
    type,
    billNo,
    commonHeaders,
    loadList,
    page,
  ]);

  const handleBill = useCallback(async () => {
    const ids = Object.values(selected)
      .map((x) => toInt(x?.id, 0))
      .filter((x) => x > 0);

    if (!canUse(ids.length > 0, "Please select at least one record")) return;
    if (!window.confirm("Are you sure?")) return;

    const selectedRows = list.filter((r, idx) => {
      const id = getRowId(r, idx);
      return ids.includes(id);
    });

    const totalAmount = selectedRows.reduce(
      (sum, r) => sum + numOr0(r?.amount),
      0
    );

    try {
      setBillingLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/addSelectedToBill`,
        {
          branchId,
          loginUserId,
          vehicleId,
          type,
          billNo,
          ids,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Add to bill failed");
      }

      toast.success(data?.message || "Added to bill");

      const handoff = {
        vehicleId,
        type,
        billNo,
        selectedIds: ids,
        totalAmount,
        billAdvanceId: data?.billAdvanceId,
        billAdvanceDetId: data?.billAdvanceDetId,
        billAdvanceTotal: data?.billAdvanceTotal ?? totalAmount,
      };

      goToBillGenerationWithData(
        handoff,
        ids,
        data?.billAdvanceTotal ?? totalAmount
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Add to bill failed"
      );
    } finally {
      setBillingLoading(false);
    }
  }, [
    selected,
    canUse,
    list,
    getRowId,
    branchId,
    loginUserId,
    vehicleId,
    type,
    billNo,
    commonHeaders,
    goToBillGenerationWithData,
  ]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">BILL GENERATION</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {pageTitle}
              </div>

              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"} {" • "} Vehicle ID: {vehicleId || "-"} {" • "}
                Type: {entryTypeLabel}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Selected: {checkedCount}</span>
                <span className={pillClass}>Records: {total}</span>
                <span className={pillClass}>
                  Page: {page} / {totalPages}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <FaArrowLeft />
                  Back
                </span>
              </button>
            </div>
          </div>

          {/* Entry Form */}
          <div className={sectionClass}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.dateInput}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, dateInput: e.target.value }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, amount: e.target.value }))
                  }
                  className={inputClass}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  disabled={isBusy}
                />
              </div>

              <div>
                <label className={labelClass}>Bill No</label>
                <div className={valueBoxClass}>{billNo || "-"}</div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                disabled={isBusy}
                onClick={handleSubmit}
                className={`flex items-center justify-center gap-2 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  savingLoading
                    ? "opacity-60 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaSave />
                {savingLoading
                  ? form.id
                    ? "Updating..."
                    : "Saving..."
                  : form.id
                  ? "Update"
                  : "Submit"}
              </button>

              <button
                type="button"
                onClick={clearForm}
                disabled={isBusy}
                className={`px-6 py-2 rounded-lg font-semibold border transition-colors ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Grid */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-800"
                }`}
              >
                {entryTypeLabel} List
              </div>

              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Vehicle ID:{" "}
                <span className="font-semibold">{vehicleId || "-"}</span>
                {" • "}
                Records: <span className="font-semibold">{total}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-[900px] w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">Check</th>
                    <th className="px-4 py-3 text-center">S.No</th>
                    <th className="px-4 py-3 text-center">Date</th>
                    <th className="px-4 py-3 text-center">Amount</th>
                    <th className="px-4 py-3 text-center">Edit</th>
                  </tr>
                </thead>

                <tbody
                  className={
                    isDark
                      ? "bg-gray-900 text-gray-100"
                      : "bg-white text-gray-800"
                  }
                >
                  {list.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        {gridLoading || pageLoading
                          ? "Loading..."
                          : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    list.map((row, idx) => {
                      const id = getRowId(row, idx);
                      const checked = selected[id] !== undefined;

                      return (
                        <tr
                          key={`${id}-${idx}`}
                          className={
                            isDark
                              ? "border-t border-gray-800 hover:bg-gray-800/60"
                              : "border-t border-gray-100 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(row, idx)}
                              disabled={isBusy}
                            />
                          </td>

                          <td className="px-4 py-3 text-center">
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {isoToDDMMYYYY(row?.date)}
                          </td>

                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {fmtMoney(row?.amount)}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleEditRow(row)}
                              disabled={isBusy}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg font-semibold ${
                                isDark
                                  ? "bg-blue-900/50 text-blue-100 hover:bg-blue-900/70"
                                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={handleDeleteSelected}
                        disabled={isBusy || checkedCount === 0}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow disabled:opacity-60"
                      >
                        <FaTrash />
                        {deletingLoading ? "Deleting..." : "Delete"}
                      </button>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => loadList(Math.max(1, page - 1))}
                        disabled={isBusy || page <= 1}
                        className={`px-3 py-2 rounded border text-sm ${
                          isDark
                            ? "border-gray-600 text-gray-200 disabled:opacity-50"
                            : "border-gray-300 text-gray-700 disabled:opacity-50"
                        }`}
                      >
                        Prev
                      </button>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-sm ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Page <b>{page}</b> / <b>{totalPages}</b>
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => loadList(Math.min(totalPages, page + 1))}
                        disabled={isBusy || page >= totalPages}
                        className={`px-3 py-2 rounded border text-sm ${
                          isDark
                            ? "border-gray-600 text-gray-200 disabled:opacity-50"
                            : "border-gray-300 text-gray-700 disabled:opacity-50"
                        }`}
                      >
                        Next
                      </button>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={handleBill}
                        disabled={isBusy || checkedCount === 0}
                        className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow disabled:opacity-60`}
                      >
                        <FaFileInvoiceDollar />
                        {billingLoading ? "Processing..." : "Bill"}
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {isBusy && (
              <div
                className={`mt-3 text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {pageLoading
                  ? "Loading page..."
                  : gridLoading
                  ? "Loading grid..."
                  : savingLoading
                  ? "Saving record..."
                  : deletingLoading
                  ? "Deleting records..."
                  : billingLoading
                  ? "Preparing bill..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <div
            className={`mt-4 text-xs ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Tip: open like <b>/vehicle/BillAddAdvance?VehID=2142&Type=1</b> for
            Advance or <b>?VehID=2142&Type=2</b> for Shifting.
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddAdvance;